import { customerRequest } from "./customerAuth";

export type OrderStatus =
  | "PENDING_PAYMENT"
  | "PENDING_APPROVAL"
  | "PREPARING_ORDER"
  | "DELIVERING"
  | "DELIVERED"
  | "CANCELED"
  | "WAITING_REFUND"
  | "PAYMENT_FAILED";

export type OrderDestination = "COUNTER" | "TABLE" | "GIFT";

export type PaymentMethod = "PIX" | "CREDIT_CARD" | "VOUCHER" | "BOLETO";

export type OrderComplaintStatus =
  | "NOT_CONTESTED"
  | "AWAITING_RESTAURANT_RESPONSE"
  | "AWAITING_CUSTOMER_RESPONSE"
  | "AWAITING_MARKETPLACE_RESPONSE"
  | "ORDER_MAINTAINED"
  | "ORDER_REFUNDED";

export interface CustomerOrderRestaurant {
  id: string;
  name: string;
  imageUrl: string | null;
}

export interface CustomerOrderExtra {
  id: string;
  name: string;
  price: number;
  amount: number;
}

export interface CustomerOrderItem {
  id: string;
  name: string;
  description?: string | null;
  price: number;
  amount: number;
  imageUrl?: string | null;
  note?: string | null;
  extras: CustomerOrderExtra[];
}

export interface CustomerOrderReview {
  id: string;
  stars: number;
  comment?: string | null;
  createdDate: string;
}

export interface CustomerOrderTimelineStep {
  status: OrderStatus;
  label: string;
  updatedDate: string | null;
  completed: boolean;
  current: boolean;
}

export interface CustomerOrderDisputeMessage {
  id: string;
  role: "USER" | "RESTAURANT";
  message: string;
  createdDate: string;
}

export interface CustomerOrderDispute {
  status: OrderComplaintStatus;
  isComplaintSolved?: boolean | null;
  canSolve: boolean;
  timeoutAt?: string | null;
  messages: CustomerOrderDisputeMessage[];
}

export interface CustomerOrderSummary {
  id: string;
  code: string;
  restaurant: CustomerOrderRestaurant;
  status: OrderStatus;
  destination: OrderDestination;
  tableNumber?: string | null;
  paymentMethod: PaymentMethod;
  price: number;
  fullPrice: number;
  itemsCount: number;
  firstItemName: string;
  createdDate: string;
  updatedDate: string;
  canCancel: boolean;
  canReview: boolean;
  hasDispute: boolean;
  isPendingPayment: boolean;
  isInProgress: boolean;
  isFinished: boolean;
}

export interface CustomerOrderDetail extends CustomerOrderSummary {
  serviceFee?: number | null;
  appFee?: number | null;
  card4LastNumbers?: string | null;
  cardBrand?: string | null;
  pixQrCode?: string | null;
  pixExpirationDate?: string | null;
  items: CustomerOrderItem[];
  timeline: CustomerOrderTimelineStep[];
  dispute?: CustomerOrderDispute | null;
  review?: CustomerOrderReview | null;
  canDispute: boolean;
  cancellationDate?: string | null;
  responsibleForCancellation?: string | null;
}

export interface CreateCustomerComplaintInput {
  message: string;
}

export interface CloseCustomerComplaintInput {
  isComplaintSolved: boolean;
}

export interface CreateCustomerReviewInput {
  stars: number;
  comment?: string;
}

export const customerOrdersApi = {
  list: () =>
    customerRequest<CustomerOrderSummary[]>({
      method: "GET",
      url: "customer/orders",
    }),

  getById: (orderId: string) =>
    customerRequest<CustomerOrderDetail>({
      method: "GET",
      url: `customer/orders/${encodeURIComponent(orderId)}`,
    }),

  cancel: (orderId: string) =>
    customerRequest<CustomerOrderDetail>({
      method: "POST",
      url: `customer/orders/${encodeURIComponent(orderId)}/cancel`,
    }),

  createComplaint: (orderId: string, input: CreateCustomerComplaintInput) =>
    customerRequest<CustomerOrderDetail>({
      method: "POST",
      url: `customer/orders/${encodeURIComponent(orderId)}/complaint`,
      data: input,
    }),

  closeComplaint: (orderId: string, input: CloseCustomerComplaintInput) =>
    customerRequest<CustomerOrderDetail>({
      method: "POST",
      url: `customer/orders/${encodeURIComponent(orderId)}/complaint/close`,
      data: input,
    }),

  createReview: (orderId: string, input: CreateCustomerReviewInput) =>
    customerRequest<CustomerOrderDetail>({
      method: "POST",
      url: `customer/orders/${encodeURIComponent(orderId)}/review`,
      data: input,
    }),
};
